<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card flat>
          <v-card-title>
            Customer Email Log
            <v-spacer/>
            <v-text-field
              v-model="searchEmail"
              label="Customer Email"
              placeholder="Enter email address"
              outlined
              dense
              hide-details
              clearable
              class="mr-2"
              style="max-width: 340px;"
              @keyup.enter="search"
            />
            <v-autocomplete
              v-model="searchTemplate"
              :items="templates"
              item-value="id"
              label="Email Template"
              placeholder="Filter by template"
              outlined
              dense
              hide-details
              clearable
              class="mr-2"
              style="max-width: 280px;"
              :loading="loadingTemplates"
              @keyup.enter="search"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.id }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.description">{{ item.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item }">
                {{ item.id }}
              </template>
            </v-autocomplete>
            <v-btn
              color="primary"
              @click="search"
              :loading="loading"
              :disabled="!searchEmail && !searchTemplate"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>

          </v-card-title>

          <v-divider/>

          <v-card-text>
            <v-alert v-if="error" type="error" dismissible @input="error = null">
              {{ error }}
            </v-alert>

            <v-alert v-if="!loading && emails.length === 0 && searchDescription" type="info">
              No emails found for {{ searchDescription }}
            </v-alert>

            <v-list v-if="emails.length > 0" two-line>
              <v-list-item
                v-for="email in emails"
                :key="email.id"
                @click="viewEmail(email)"
                class="email-list-item"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ email.templateId }}
                    <v-chip
                      v-if="email.success"
                      x-small
                      color="success"
                      class="ml-2"
                    >
                      Sent
                    </v-chip>
                    <v-chip
                      v-else
                      x-small
                      color="error"
                      class="ml-2"
                    >
                      Failed
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatDate(email.sentAt) }}
                    <span v-if="email.to" class="ml-2 grey--text">{{ email.to }}</span>
                    <span v-if="email.version" class="ml-2 text-caption grey--text">
                      Version: {{ formatVersionDate(email.version) }}
                    </span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="email.errors && email.errors.length > 0" class="error--text text-caption">
                    {{ email.errors.map(e => e.type || e.message || e).join(' · ') }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="email.messageId" class="text-caption grey--text">
                    Message ID: {{ email.messageId }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon>mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from '../api';
import moment from 'moment';
import {dateFormatted} from '../store/utils';
import urlState from '../router/urlState';

export default {
  name: 'CustomerEmailLog',
  mixins: [urlState],
  data() {
    return {
      searchEmail: '',
      searchTemplate: null,
      searchedEmail: '',
      searchedTemplate: null,
      templates: [],
      loadingTemplates: false,
      emails: [],
      loading: false,
      error: null,
    };
  },
  computed: {
    searchDescription() {
      const parts = [];
      if (this.searchedEmail) parts.push(this.searchedEmail);
      if (this.searchedTemplate) parts.push(`template "${this.searchedTemplate}"`);
      return parts.join(' / ');
    },
  },
  async mounted() {
    this.syncToUrl({
      param: 'searchedEmail',
      urlParam: 'email',
      initFromRoute: true,
    });
    this.syncToUrl({
      param: 'searchedTemplate',
      urlParam: 'template',
      initFromRoute: true,
    });

    this.loadTemplates();

    await this.$nextTick();
    if (this.searchedEmail || this.searchedTemplate) {
      this.searchEmail = this.searchedEmail || '';
      this.searchTemplate = this.searchedTemplate || null;
      await this.loadEmails();
    }
  },
  watch: {
    searchedEmail(email) {
      if (this.searchedEmail !== email) {
        this.searchedEmail = email;
        this.loadEmails();
      }
    }
  },
  methods: {
    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const response = await api.get('/v2/email-templates');
        this.templates = response.data;
      } catch (e) {
        // non-fatal, autocomplete just won't have items
      } finally {
        this.loadingTemplates = false;
      }
    },

    search() {
      this.loadEmails();
    },

    async loadEmails() {
      if (!this.searchEmail?.trim() && !this.searchTemplate) return;

      this.loading = true;
      this.error = null;
      this.emails = [];

      try {
        const params = {limit: 100};
        if (this.searchEmail?.trim()) params.email = this.searchEmail.trim();
        if (this.searchTemplate) params.templateId = this.searchTemplate;

        const response = await api.get('/v2/email-log', {params});

        this.emails = response.data;
        this.searchedEmail = this.searchEmail?.trim() || '';
        this.searchedTemplate = this.searchTemplate || null;
      } catch (error) {
        console.error('Failed to load emails:', error);
        this.error = error.response?.data?.error || 'Failed to load email log';
      } finally {
        this.loading = false;
      }
    },

    viewEmail(email) {
      this.$router.push({name: 'CustomerEmailDetail', params: {logId: email.id}});
    },

    formatDate(isoString) {
      return dateFormatted(isoString, {formatString: 'LLL'});
    },

    formatVersionDate(version) {
      if (!version) return '';
      const match = version.match(/v_(\d+)/);
      if (!match) return version;
      return moment(parseInt(match[1], 10)).format(moment.HTML5_FMT.DATETIME_LOCAL);
    },
  },
};
</script>

<style scoped>
.email-list-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.email-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
